import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslations } from '@/app/i18n.js';

function AsideMenu({ steps, currentStep, setCurrentStep }) {
    const __ = useTranslations();
    const [floatingIndicatorStyle, setFloatingIndicatorStyle] = useState({ top: 0, height: 0 });
    const menuItemsRef = useRef({});

    const updateFloatingIndicator = useCallback(() => {
        let currentFloating = currentStep;
        if (currentStep === 'CATEGORY') {
            currentFloating = 'GAME';
        }
        const currentElement = menuItemsRef.current[currentFloating];
        if (currentElement) {
            setFloatingIndicatorStyle({
                top: currentElement.offsetTop,
                height: currentElement.offsetHeight,
            });
        }
    }, [currentStep]);

    // Update floating indicator position when currentStep or steps change
    useEffect(() => {
        const id = setTimeout(() => {
            updateFloatingIndicator();
        }, 100);
        return () => clearTimeout(id);
    }, [currentStep, steps, updateFloatingIndicator]);

    // Initial position and resize listener
    useEffect(() => {
        const id = setTimeout(() => {
            updateFloatingIndicator();
        }, 400);
        return () => clearTimeout(id);
    }, [updateFloatingIndicator]);

    useEffect(() => {
        window.addEventListener('resize', updateFloatingIndicator);
        return () => {
            window.removeEventListener('resize', updateFloatingIndicator);
        };
    }, [updateFloatingIndicator]);

    return (
        <div className="flex flex-col relative">
            {/* Active State Indicator */}
            <div
                className="absolute left-0 right-0 bg-gray-50 dark:bg-gray-915 dark:highlight-shadow transition-all duration-200 ease-in-out border-l-4 dark:border-l border-primary"
                style={{
                    top: floatingIndicatorStyle.top,
                    height: floatingIndicatorStyle.height,
                }}
            />

            {/* Menu Items */}
            {Object.keys(steps).filter(s => s !== 'CATEGORY').map((step) => (
                <div
                    key={step}
                    ref={el => menuItemsRef.current[step] = el}
                    onClick={() => {
                        if (steps[step].internal !== null && step !== currentStep) {
                            setCurrentStep(step);
                        }
                    }}
                    className={`
                        relative py-3 dark:my-1 px-6
                        flex flex-col justify-center
                        transition-colors duration-200
                        border-t border-gray-300 dark:border-t-0
                        ${step === currentStep ? 'text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}
                        ${steps[step].internal !== null && step !== currentStep ? 'cursor-pointer hover:bg-gray-50 hover:text-gray-800 dark:hover:text-gray-100 dark:hover:bg-gray-925' : ''}
                    `}
                >
                    {/* Main Content */}
                    <div className="flex flex-row items-center justify-between relative">
                        <div className="flex flex-row items-center">
                            <span className="text-base font-semibold transition-colors duration-200">
                                {steps[step].menu}
                            </span>
                        </div>

                        {/* Edit Button */}
                        {steps[step].internal !== null && step !== currentStep && (
                            <div
                                className="absolute flex right-0 top-0 rounded-full cursor-pointer border border-gray-300 hover:bg-gray-915 hover:text-gray-100 dark:border-0 dark:bg-gray-915 dark:hover:bg-dark-gray transition duration-200"
                                onClick={() => setCurrentStep(step)}
                            >
                                <span className="text-xxs uppercase px-2 py-1.5 leading-none">
                                    {__('book.common.modify')}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Game Image */}
                    {steps[step].internal !== null && step === 'GAME' && (
                        <div className="relative">
                            <img
                                src={steps[step].internal.main_poster}
                                srcSet={steps[step].internal.mainPosterSrcset}
                                alt={steps[step].internal.name}
                                sizes="1px"
                                onLoad={(e) => {
                                    if (e.target.dataset.sized === undefined) {
                                        e.target.sizes = Math.ceil(((e.target.getBoundingClientRect().width / window.innerWidth) * 100) * 1.5) + 'vw';
                                        e.target.dataset.sized = '';
                                        e.target.classList.remove('blurred');
                                    }
                                }}
                                loading="lazy"
                                className="w-full rounded dark:warm-shadow border border-light-gray mt-2.5 mb-2.5"
                            />
                            <div className="absolute bottom-4 w-full">
                                <img
                                    src={steps[step].internal.logo}
                                    srcSet={steps[step].internal.logoSrcset}
                                    alt={steps[step].internal.name}
                                    className="w-32 mx-auto"
                                    style={{whiteSpace: 'nowrap'}}
                                    sizes="16vw"
                                    onLoad={(e) => {
                                        if (e.target.dataset.sized === undefined) {
                                            e.target.sizes = Math.ceil((e.target.getBoundingClientRect().width / window.innerWidth) * 100) + 'vw';
                                            e.target.dataset.sized = ''
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Value Content */}
                    <div className="flex flex-col mt-1.5 dark:text-light-gray">
                        {steps[step].value.title && (
                            <span
                                className={`
                                dark:font-bold mb-1 whitespace-pre-line capitalize
                                ${step === 'LOCATION' || step === 'DATE' ? 'text-sm' : ''}
                                ${step === 'GAME' ? 'text-md' : ''}
                                ${step === 'TEAM' ? 'text-lg' : ''}
                            `}>
                                {steps[step].value.title}
                                {step === 'TEAM' && steps[step].internal && steps[step].internal > 1 && ` ${__('book.common.players')}`}
                                {step === 'TEAM' && steps[step].internal && steps[step].internal <= 1 && ` ${__('book.common.player')}`}
                            </span>
                        )}


                        {steps[step].value.subtitle && (
                            <span
                                className={`${step === 'GAME' ? 'dark:text-light-gray text-xs mb-1.5' : 'dark:text-white text-base'} whitespace-pre-line leading-none`}>
                                {steps[step].value.subtitle}
                            </span>
                        )}

                        {step === 'GAME' && (step === currentStep || currentStep === 'CATEGORY') && (
                            <div className="flex flex-col my-1 gap-1">
                                <div className="flex flex-row gap-1 border border-medium-gray dark:border-0 dark:bg-medium-gray text-xs py-2 px-3 rounded">
                                    <svg className="size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
                                        <rect width="256" height="256" fill="none"/>
                                        <circle cx="128" cy="128" r="96" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                                        <path d="M120,120a8,8,0,0,1,8,8v40a8,8,0,0,0,8,8" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                                        <circle cx="124" cy="84" r="12" fill="currentColor"/>
                                    </svg>
                                    <span>{__('book.index.steps.game.help_text')}</span>
                                </div>
                                <span className="dark:text-gray-200 text-xs my-1 mt-2 inline-block">{__('book.index.steps.game.help_text_2')}</span>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default AsideMenu;
